import React from 'react';
import PropTypes from 'prop-types';
import { DropTarget } from 'react-dnd';
import TreatmentInstance from '../../../containers/DailyPlanning/TreatmentInstance/TreatmentInstanceContainer';
import Spinner from '../../common/Spinner/Spinner';
import globals from '../../../globals/communityGlobals';
import { ASSIGNMENT } from '../../../components/DailyPlanning/TimelineAssignment/TimelineAssignment';

import treatmentInstancePropType from '../../../propTypes/entities/treatmentInstance';

const locale = globals.getArea('dailyPlanning');

const styles = {
  cardList: { boxShadow: 'inset 0 0 10px #86c5da' },
  list: { textAlign: 'center', padding: '10px 0' },
};

const treatmentInstanceListTarget = {
  canDrop() {
    return true;
  },
  drop(props, monitor) {
    if (monitor.didDrop()) {
      return false;
    }
    return {};
  },
};

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    canDrop: monitor.canDrop(),
    isOver: monitor.isOver(),
    isDragging: monitor.isDragging,
    itemType: monitor.getItemType(),
  };
}

function getRequiredSkills(activities) {
  return activities.reduce((ids, activity) => ids.concat(activity.skills.map(s => s.id)), []);
}

class TreatmentInstanceList extends React.Component {
  constructor(props) {
    super(props);
    this.defaultTimeSlots = [];
  }
  shouldComponentUpdate = nextProps =>
    this.props.isFetching !== nextProps.isFetching ||
    this.props.isAutoAllocating !== nextProps.isAutoAllocating ||
    this.props.autoAllocateDate !== nextProps.autoAllocateDate ||
    this.props.autoAllocateTeamId !== nextProps.autoAllocateTeamId ||
    this.props.canDrop !== nextProps.canDrop ||
    this.props.treatmentInstances.map(ti => ti.treatmentInstanceId).join(',') !==
      nextProps.treatmentInstances.map(ti => ti.treatmentInstanceId).join(',');

  getCardListStyle = () => ({ boxShadow: this.props.canDrop && 'inset 0 0 10px #86c5da' });

  render() {
    const {
      isFetching,
      isAutoAllocating,
      autoAllocateDate,
      autoAllocateTeamId,
      selectSelectedDate,
      selectSelectedTeam,
      treatmentInstances,
      connectDropTarget,
      canAllocateWork,
      overrideClinician,
      removeOverrideClinician,
    } = this.props;
    let treatmentInstancesList = null;
    if (
      isFetching ||
      (isAutoAllocating && selectSelectedDate === autoAllocateDate && selectSelectedTeam === autoAllocateTeamId)
    ) {
      treatmentInstancesList = <Spinner />;
    } else if (treatmentInstances.length < 1) {
      treatmentInstancesList = <div style={styles.list}>{locale.getMsg('noTreatmentsRequired')}</div>;
    } else {
      treatmentInstancesList = treatmentInstances.map(
        ({
          treatmentInstanceId,
          treatmentId,
          patientFullName,
          nhsNumber,
          ragStatus,
          activities,
          activitiesTotalDuration,
          highestGrade,
          timeSlots = this.defaultTimeSlots,
          cardType,
          copiesCount,
          wasUnassignedBySynchronization,
          isSingleTimeSlot,
          masterId,
          occurrence,
          effectiveTimeSlots,
          isLastChild,
          isFirstChild,
          isSpecificTime,
          isMasterAssigned,
          address,
          additionalCliniciansCount,
          isTwilightTreatment,
          excludedClinicians,
          assignGuid,
          hasVarianceFactorChanged,
        }) => (
          <TreatmentInstance
            key={`${treatmentInstanceId.toString()}|${treatmentId.toString()}|${occurrence}`}
            assignGuid={assignGuid}
            occurrence={occurrence}
            uniqueTreatmentId={treatmentInstanceId.toString() + treatmentId.toString() + occurrence}
            treatmentInstanceId={treatmentInstanceId}
            treatmentId={treatmentId}
            patientName={patientFullName}
            nhsNumber={nhsNumber}
            location={address.postCode}
            ragStatus={ragStatus.name}
            activities={activities}
            requiredTime={activitiesTotalDuration}
            requiredGrade={highestGrade}
            requiredSkills={getRequiredSkills(activities)}
            timeSlots={timeSlots}
            canAllocateWork={canAllocateWork}
            cardType={cardType}
            wasUnassignedBySynchronization={wasUnassignedBySynchronization}
            copiesCount={copiesCount}
            additionalCliniciansCount={additionalCliniciansCount}
            isSingleTimeSlot={isSingleTimeSlot}
            effectiveTimeSlots={effectiveTimeSlots}
            isFirstChild={isFirstChild}
            isLastChild={isLastChild}
            isMasterAssigned={isMasterAssigned}
            overrideClinician={overrideClinician}
            removeOverrideClinician={removeOverrideClinician}
            isSpecificTime={isSpecificTime}
            masterId={masterId}
            isTwilightTreatment={isTwilightTreatment}
            excludedClinicians={excludedClinicians}
            hasVarianceFactorChanged={hasVarianceFactorChanged}
          />
        ),
      );
    }

    return connectDropTarget(
      <div className="card-list" style={this.getCardListStyle()}>
        {treatmentInstancesList}
      </div>,
    );
  }
}

TreatmentInstanceList.defaultProps = {
  treatmentInstances: [],
  filteredTreatments: [],
  autoAllocateTeamId: null,
  autoAllocateDate: '',
  selectSelectedDate: '',
  selectSelectedTeam: null,
};

TreatmentInstanceList.propTypes = {
  connectDropTarget: PropTypes.func.isRequired,
  canAllocateWork: PropTypes.bool.isRequired,
  treatmentInstances: PropTypes.arrayOf(treatmentInstancePropType).isRequired,
  isAutoAllocating: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  canDrop: PropTypes.bool.isRequired,
  overrideClinician: PropTypes.func.isRequired,
  removeOverrideClinician: PropTypes.func.isRequired,
  selectSelectedDate: PropTypes.string,
  selectSelectedTeam: PropTypes.number,
  autoAllocateTeamId: PropTypes.number,
  autoAllocateDate: PropTypes.string,
};

export default DropTarget(ASSIGNMENT, treatmentInstanceListTarget, collect)(TreatmentInstanceList);
